import { CommonModule } from '@angular/common';
import {
  ChangeDetectionStrategy,
  Component,
  effect,
  input,
  output,
} from '@angular/core';
import { takeUntilDestroyed, toObservable } from '@angular/core/rxjs-interop';
import { ReactiveFormsModule } from '@angular/forms';
import { MatFormFieldModule } from '@angular/material/form-field';
import { MatSelectModule } from '@angular/material/select';
import {
  formControlChanges$,
  TypedFormControl,
} from '@principle-theorem/ng-shared';
import {
  ICustomReportFilter,
  ReportFilterComparison,
  ReportFilterOption,
} from '@principle-theorem/principle-core/interfaces';
import { QueryOptionResolver } from '@principle-theorem/reporting';
import { debounceUserInput, filterUndefined } from '@principle-theorem/shared';
import { first } from 'lodash';
import { take } from 'rxjs/operators';

@Component({
  selector: 'pr-edit-report-filter-boolean',
  templateUrl: './edit-report-filter-boolean.component.html',
  styleUrls: ['./edit-report-filter-boolean.component.scss'],
  changeDetection: ChangeDetectionStrategy.OnPush,
  imports: [
    CommonModule,
    MatFormFieldModule,
    MatSelectModule,
    ReactiveFormsModule,
  ],
})
export class EditReportFilterBooleanComponent {
  filter = input.required<ICustomReportFilter>();
  filterChange = output<Partial<ICustomReportFilter>>();
  options = QueryOptionResolver.boolean();
  valueCtrl = new TypedFormControl<ReportFilterOption>();

  constructor() {
    formControlChanges$(this.valueCtrl)
      .pipe(filterUndefined(), debounceUserInput(), takeUntilDestroyed())
      .subscribe((value) =>
        this.filterChange.emit({
          values: [value],
          comparison: ReportFilterComparison.IsEqualTo,
          dateType: undefined,
          relativeOffset: undefined,
        })
      );

    effect(() =>
      this.valueCtrl.setValue(this._getFilterValue(this.filter()), {
        emitEvent: false,
      })
    );

    toObservable(this.filter)
      .pipe(filterUndefined(), take(1), takeUntilDestroyed())
      .subscribe((filter) =>
        this.valueCtrl.setValue(this._getFilterValue(filter))
      );
  }

  private _getFilterValue(filter: ICustomReportFilter): ReportFilterOption {
    const selectedValue = first(filter.values);
    const defaultValue = this.options[0];

    if (!selectedValue) {
      return defaultValue;
    }

    return (
      this.options.find((option) => option.value === selectedValue.value) ||
      defaultValue
    );
  }
}
